/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import './licensing-page-status-card.component.less';

import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Type,
} from '@angular/core';

import {
    ALBServicesConnectivityStatus,
    ALBServicesRegistrationStatus,
    IALBServicesConfig,
    ICLSStatus,
    ICluster,
    LicenseTierType,
} from 'generated-types';

import {
    combineLatest,
    Subject,
    take,
    takeUntil,
} from 'rxjs';

import { ClusterItem } from 'items/cluster.item.factory';
import { defaultPortalUrl } from 'ajs/modules/cportal/constants';
import { CportalService } from 'ajs/modules/cportal';
import { DialogService } from 'ng/modules/core/services/dialog.service';
import { L10nService } from '@vmw/ngx-vip';
import { Store } from '@ngrx/store';
import * as globalL10n from 'global-l10n';
import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';

import {
    AviContinueConfirmationComponent,
} from 'ng/modules/dialog/components/avi-continue-confirmation/avi-continue-confirmation.component';

import { AlertType, IOnPremLicensingDetails } from '../../licensing.types';
import { DisconnectCloudLicensingModalComponent } from '../disconnect-cloud-licensing-modal';
import { DisconnectOnPremLicensingModalComponent } from '../disconnect-on-prem-licensing-modal';
import { LicensingPageStore } from '../licensing-page.store';
import * as l10n from './licensing-page-status-card.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const CANCEL_REGISTRATION_DIALOG_ID = 'CANCEL_REGISTRATION_DIALOG_ID';
const DISCONNECT_FROM_CLOUD_LICENSING = 'DISCONNECT_CLOUD_LICENSING';
const DISCONNECT_FROM_ON_PREM_LICENSING = 'DISCONNECT_ON_PREM_LICENSING';

type TCluster = typeof ClusterItem;

interface ICLSStatusExtended extends ICLSStatus {
    cls_uuid?: string;
}

/**
 * @description Component to display licensing card with status information.
 * @author Shubam Bindal, Deepak Chaudhary
 */
@Component({
    selector: 'licensing-page-status-card',
    templateUrl: './licensing-page-status-card.component.html',
})
export class LicensingPageStatusCardComponent implements OnInit, OnDestroy {
    @Input()
    public headerTitle = '';

    /**
     * Emit event when tier change modal should be opened.
     */
    @Output()
    public onLicenseTypeChangeModal = new EventEmitter<void>();

    /**
     * Emit error to parent component for display.
     */
    @Output()
    public onError = new EventEmitter<string>();

    /**
     * Used to check if controller is connected with the Cloud Console (Pulse Portal).
     */
    public aviCloudConnected = false;

    /**
     * Used to check if controller is Registered with the pulse.
     */
    public isControllerRegistered = false;

    /**
     * Connectivity status text.
     */
    public connectivityStatusText = '';

    /**
     * License Connection Last refresh time.
     */
    public lastRefresh = '';

    public error = '';

    public portalUrl = '';

    public clusterName = '-';

    public busy = false;

    public isOnPremLicensing = false;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly saasStatus$ = this.licensingPageStore.saasStatus$;

    public readonly clsStatus$ = this.licensingPageStore.clsStatus$;

    public deregisterLoading!: boolean;

    public onPremLicensingServiceUrl = '';

    public defaultLicenseTier: LicenseTierType;

    /**
     * On Prem Licensing uuid (Used for unsubscribe).
     */
    private clsUuid = '';

    /**
     * Instance of Cluster Item
     */
    private cluster: ClusterItem = null;

    private licensingConnectivityStatus = false;

    private isLicensingEnabled = false;

    /**
     * Subject used to unsubscribe observables.
     */
    private readonly ngDestroyed$ = new Subject();

    constructor(
        private readonly l10nService: L10nService,
        private readonly cPortalService: CportalService,
        @Inject('Cluster')
        private readonly Cluster: TCluster,
        private readonly store: Store,
        private readonly dialogService: DialogService,
        private readonly licensingPageStore: LicensingPageStore,
    ) {
        l10nService.registerSourceBundles(dictionary);

        const seriesInfoOnSelectedValue$ = combineLatest([
            this.licensingPageStore.saasStatus$,
            this.licensingPageStore.clsStatus$,
            this.licensingPageStore.connectivityStatus$,
            this.licensingPageStore.registrationStatus$,
            this.licensingPageStore.tierInUse$,
        ]);

        seriesInfoOnSelectedValue$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(([saasStatus, clsStatus, connectivityStatus, registrationStatus, tierInUse]) => {
                this.defaultLicenseTier = tierInUse;

                if (tierInUse === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES) {
                    this.licensingConnectivityStatus = saasStatus?.connected ?? false;
                    this.lastRefresh = saasStatus?.refreshed_at ?? '';

                    this.isControllerRegistered =
                        registrationStatus === ALBServicesRegistrationStatus.ALBSERVICES_REGISTERED;
                    this.aviCloudConnected = connectivityStatus === ALBServicesConnectivityStatus.ALBSERVICES_CONNECTED;
                    this.isLicensingEnabled = saasStatus?.enabled;
                    this.isOnPremLicensing = false;
                } else {
                    this.licensingConnectivityStatus = clsStatus?.connected ?? false;
                    this.lastRefresh = clsStatus?.refreshed_at ?? '';
                    this.isLicensingEnabled = clsStatus?.enabled;
                    this.isOnPremLicensing = true;

                    if (this.isLicensingEnabled && !this.clsUuid) {
                        this.loadOnPremLicensingDetails();
                    }
                }

                this.connectivityStatusText = this.licensingConnectivityStatus ?
                    this.l10nService.getMessage(l10nKeys.connectedLabel) :
                    this.l10nService.getMessage(l10nKeys.disconnectedLabel);
            });

        this.licensingPageStore.onPremLicensingDetails$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((onPremDetails: IOnPremLicensingDetails) => {
                this.onPremLicensingServiceUrl = onPremDetails?.hostname ?? '';
            });
    }

    public get connectivityStatus(): AlertType {
        return this.licensingConnectivityStatus ? AlertType.success : AlertType.danger;
    }

    /**
     * Return true if controller is unregistered, connected, and cloud licensing is not enabled.
     */
    public get isUnregisteredAndConnected(): boolean {
        return !this.isControllerRegistered && this.aviCloudConnected &&
            !this.isOnPremLicensing && !this.isLicensingEnabled;
    }

    public get canDeregisterCloud(): boolean {
        return this.isLicensingEnabled && !this.isOnPremLicensing;
    }

    public get canSubscribeCloud(): boolean {
        return this.isControllerRegistered && this.aviCloudConnected &&
            !this.isLicensingEnabled && !this.isOnPremLicensing;
    }

    public get canDeregisterOnPrem(): boolean {
        return this.isOnPremLicensing && this.isLicensingEnabled;
    }

    public get showFooterSection(): boolean {
        return this.isUnregisteredAndConnected || this.canDeregisterOnPrem ||
            this.canDeregisterCloud || this.canSubscribeCloud;
    }

    /**
     * Return true if ENTERPRISE_WITH_CLOUD_SERVICES tier is selected.
     */
    public get isEnterpriseWithCloudServicesTier(): boolean {
        return this.defaultLicenseTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES;
    }

    /**
     * @override
     */
    public ngOnInit(): void {
        this.store.select(SystemInfoSelectors.selectLocalCluster)
            .pipe(take(1))
            .subscribe((cluster: ICluster) => {
                this.clusterName = cluster?.name ?? '-';
            });

        this.setPortalUrl();
        this.cluster = new this.Cluster({ id: 'default' });
    }

    /**
     * Open the license tier change modal instead of registration modal.
     */
    public handleCloudLicensingRegistration(): void {
        this.onLicenseTypeChangeModal.emit();
    }

    /**
     * Function to de-register Controller with Portal
     * Opens disconnect confirmation dialog with checkbox option.
     */
    public deregisterCloudLicensing(): void {
        const headerTitle = this.l10nService.getMessage(l10nKeys.disconnectFromCloudServiceLabel);

        this.dialogService.add({
            id: DISCONNECT_FROM_CLOUD_LICENSING,
            component: DisconnectCloudLicensingModalComponent as Type<Component>,
            componentProps: {
                headerTitle,
                onConfirm: async(disconnectFromCloudServices: boolean) => {
                    this.deregisterLoading = true;
                    this.dialogService.remove(DISCONNECT_FROM_CLOUD_LICENSING);
                    await this.handleDisconnect(() => this.licensingPageStore
                        .disconnectCloudLicensing(disconnectFromCloudServices));
                    this.deregisterLoading = false;
                },
                onClose: () => {
                    this.dialogService.remove(DISCONNECT_FROM_CLOUD_LICENSING);
                },
            },
        });
    }

    /**
     * Function to disconnect from on-prem license hub.
     * Opens disconnect confirmation dialog with force disconnect option.
     */
    public disconnectFromOnPrem(): void {
        this.dialogService.add({
            id: DISCONNECT_FROM_ON_PREM_LICENSING,
            component: DisconnectOnPremLicensingModalComponent as Type<Component>,
            componentProps: {
                onConfirm: async() => {
                    this.deregisterLoading = true;
                    this.dialogService.remove(DISCONNECT_FROM_ON_PREM_LICENSING);

                    this.licensingPageStore.clsStatus$
                        .pipe(take(1))
                        .subscribe((clsStatus: ICLSStatusExtended) => {
                            this.clsUuid = clsStatus?.cls_uuid ?? '';
                        });

                    await this.handleDisconnect(() => this.licensingPageStore
                        .disconnectOnPremLicensing(this.clsUuid));

                    this.deregisterLoading = false;
                },
                onClose: () => {
                    this.dialogService.remove(DISCONNECT_FROM_ON_PREM_LICENSING);
                },
            },
        });
    }

    /**
     * Clear the registration state and disconnects from Cloud Services.
     */
    public handleCancelRegistration(): void {
        this.dialogService.add({
            id: CANCEL_REGISTRATION_DIALOG_ID,
            component: AviContinueConfirmationComponent as Type<Component>,
            componentProps: {
                customHeader: this.l10nService.getMessage(l10nKeys.cancelRegistrationLabel),
                warning: this.l10nService.getMessage(l10nKeys.cancelRegistrationMessage),
                onConfirm: async() => {
                    this.deregisterLoading = true;
                    this.dialogService.remove(CANCEL_REGISTRATION_DIALOG_ID);
                    await this.handleDisconnect(() => this.licensingPageStore.clearRegistration());
                    this.deregisterLoading = false;
                },
                onClose: () => {
                    this.dialogService.remove(CANCEL_REGISTRATION_DIALOG_ID);
                },
            },
        });
    }

    /**
     * @override
     */
    public ngOnDestroy(): void {
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();

        if (this.cluster) {
            this.cluster.destroy();
        }
    }

    /**
     * Function to set Portal Url.
     */
    private setPortalUrl(): void {
        this.cPortalService.getPortalInfo()
            .then(({ portal_url: portalUrl }: IALBServicesConfig) => {
                this.portalUrl = portalUrl ?? defaultPortalUrl;
            }).catch((error: string) => this.error = error);
    }

    /**
     * Load on-prem licensing details from the store.
     */
    private loadOnPremLicensingDetails(): void {
        this.licensingPageStore.clsStatus$
            .pipe(take(1))
            .subscribe((clsStatus: ICLSStatusExtended) => {
                this.clsUuid = clsStatus?.cls_uuid ?? '';

                if (this.clsUuid) {
                    this.licensingPageStore.loadOnPremLicensingDetails(this.clsUuid);
                }
            });
    }

    /**
     * Common handler for disconnect operations (cloud and on-prem).
     * Handle busy state, error handling, and refreshing status after disconnect.
     * @param disconnectFn - The disconnect function to execute
     */
    private async handleDisconnect(disconnectFn: () => Promise<void>): Promise<void> {
        this.busy = true;

        try {
            await disconnectFn();
            await this.licensingPageStore.loadConnectivityStatus();
            await this.licensingPageStore.loadLicenseStatus();
        } catch (error) {
            const errorObj = error as { error?: string };
            const errorMessage = errorObj?.error ||
                this.l10nService.getMessage(l10nKeys.defaultErrorMessage);

            this.onError.emit(errorMessage);
        } finally {
            this.busy = false;
        }
    }
}
